import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const differentiators = [
  {
    title: 'Results, Not Vanity Metrics',
    description: "We don't chase likes and followers. We track customers, orders, and revenue.",
  },
  {
    title: 'Instagram-Only Specialization',
    description: "We don't spread thin across 10 platforms. Instagram is all we do, and we do it better than anyone.",
  },
  {
    title: 'System-Driven Approach',
    description: "No guesswork. We follow a proven system that delivers predictable, repeatable results.",
  },
  {
    title: 'Built for Local Businesses',
    description: "Our strategies are designed specifically for restaurants, cafes, and local shops — not big brands.",
  },
  {
    title: 'Complete Growth Partner',
    description: "Strategy, content, influencers, optimization — we handle everything so you can focus on your business.",
  },
];

export const WhyUsSection = () => {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="py-24 md:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5"
          >
            <span className="font-accent text-slate-400 mb-4 block" data-testid="why-us-label">
              Why Big Jaguar
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-tight mb-8"
              data-testid="why-us-headline"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Why Businesses{' '}
              <span className="font-medium">Choose Us</span>
            </h2>
            <div className="rounded-2xl overflow-hidden" data-testid="why-us-image">
              <img
                src="https://images.unsplash.com/photo-1717251814719-3d047d151385?w=500&h=340&fit=crop"
                alt="Happy cafe business owner"
                className="w-full h-48 md:h-64 object-cover"
              />
            </div>
          </motion.div>

          <div className="md:col-span-7">
            <div className="space-y-0">
              {differentiators.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex gap-4 py-6 border-b border-slate-200 last:border-b-0"
                  data-testid={`why-us-item-${i}`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    <div className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center">
                      <Check size={14} className="text-white" strokeWidth={2} />
                    </div>
                  </div>
                  <div>
                    <h3
                      className="text-base font-medium text-slate-900 mb-1"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
